import { useMemo, type ReactNode } from 'react';
import { QuilttProvider } from '@quiltt/react';

import useCombinedBankData from 'modules/Settings/BankAccountsCombined/hooks/useCombinedBankData';
import useQuilttSessionToken from 'services/quiltt/queries/useQuilttSessionToken';
import type { StatementUploadTaskContext } from 'modules/common/components/StatementUpload/StatementUpload.interface';
import { InstitutionResolvabilityChecker, useResolvabilityResults } from 'services/quiltt/utils/quilttResolvability';
import {
    isAutoStatementsWithdrawn,
    useUnreliablePlaidInstitutionIds,
} from 'services/quiltt/utils/unreliableInstitutions';

function isStatementUploadContext(context: unknown): context is StatementUploadTaskContext {
    return (
        typeof context === 'object' &&
        context !== null &&
        'accounts' in context &&
        Array.isArray((context as StatementUploadTaskContext).accounts) &&
        'institution_name' in context
    );
}

export interface TaskResolvability {
    isResolvable: boolean;
    connectorId: string | null;
    connectionId: string | null;
    /** The connection is broken. Whether to ask the member to repair it is `offerAutoStatements`. */
    isConnectionLost: boolean;
    offerAutoStatements: boolean;
}

interface UseStatementUploadResolvabilityResult {
    resolvabilityMap: Record<string, TaskResolvability>;
    resolvabilityCheckers: ReactNode;
    isLoading: boolean;
}

/**
 * For each statement upload task, determines whether the institution supports
 * Quiltt auto-statements (is "resolvable"). Returns a map of taskId → resolvability info
 * and an invisible `ResolvabilityCheckers` component that must be rendered to perform the checks.
 */
// Accepts any task-like objects with an id and a context — works for both the legacy OperatorTask
// and the new member Task, since only id/context are read here.
export default function useStatementUploadResolvability(
    tasks: ReadonlyArray<{ id: string; context: unknown }>
): UseStatementUploadResolvabilityResult {
    const { institutions, isLoading: isBankDataLoading } = useCombinedBankData();
    const { data: quilttSessionToken, isLoading: isTokenLoading } = useQuilttSessionToken();
    const { results: resolvabilityResults, handleResult } = useResolvabilityResults();

    // Build a lookup from plaid institution ID → connectorId/connectionId/errorState
    const institutionMap = useMemo(() => {
        const map = new Map<
            string,
            {
                connectorId: string | null;
                connectionId: string | null;
                hasErrorRepairableConnection: boolean;
                offerAutoStatements: boolean;
            }
        >();
        institutions.forEach((inst) => {
            map.set(inst.institutionId, {
                connectorId: inst.connectorId,
                connectionId: inst.connectionId,
                hasErrorRepairableConnection: inst.hasErrorRepairableConnection,
                offerAutoStatements: inst.offerAutoStatements,
            });
        });
        return map;
    }, [institutions]);

    // Deduplicate institutions across tasks — each task's accounts share one plaid_institution_id
    const institutionsToCheck = useMemo(() => {
        const seen = new Set<string>();
        const result: { institutionId: string; institutionName: string; connectorId: string }[] = [];

        tasks.forEach((task) => {
            if (!isStatementUploadContext(task.context)) {
                return;
            }
            const plaidInstitutionId = task.context.accounts[0]?.plaid_institution_id;
            if (!plaidInstitutionId || seen.has(plaidInstitutionId)) {
                return;
            }
            seen.add(plaidInstitutionId);

            const combined = institutionMap.get(plaidInstitutionId);
            if (combined?.connectorId) {
                result.push({
                    institutionId: plaidInstitutionId,
                    institutionName: task.context.institution_name,
                    connectorId: combined.connectorId,
                });
            }
        });

        return result;
    }, [tasks, institutionMap]);

    const unreliablePlaidIds = useUnreliablePlaidInstitutionIds();

    // Build the per-task resolvability map
    const resolvabilityMap = useMemo(() => {
        const map: Record<string, TaskResolvability> = {};

        tasks.forEach((task) => {
            if (!isStatementUploadContext(task.context)) {
                return;
            }
            const plaidInstitutionId = task.context.accounts[0]?.plaid_institution_id;
            const combined = plaidInstitutionId ? institutionMap.get(plaidInstitutionId) : undefined;
            // One decision for the row's Reconnect, eligible popup and upload-modal banner.
            const isWithdrawn = isAutoStatementsWithdrawn(
                combined?.offerAutoStatements,
                plaidInstitutionId,
                unreliablePlaidIds
            );
            const isResolvable =
                !isWithdrawn && !!plaidInstitutionId && resolvabilityResults[plaidInstitutionId] === true;

            map[task.id] = {
                isResolvable,
                connectorId: combined?.connectorId ?? null,
                connectionId: combined?.connectionId ?? null,
                isConnectionLost: combined?.hasErrorRepairableConnection ?? false,
                offerAutoStatements: !isWithdrawn,
            };
        });

        return map;
    }, [tasks, institutionMap, resolvabilityResults, unreliablePlaidIds]);

    const resolvabilityCheckers = useMemo(() => {
        if (!quilttSessionToken?.token || institutionsToCheck.length === 0) {
            return null;
        }

        return (
            <QuilttProvider token={quilttSessionToken.token}>
                {institutionsToCheck.map((inst) => (
                    <InstitutionResolvabilityChecker
                        key={inst.institutionId}
                        institutionId={inst.institutionId}
                        institutionName={inst.institutionName}
                        connectorId={inst.connectorId}
                        onResult={handleResult}
                    />
                ))}
            </QuilttProvider>
        );
    }, [institutionsToCheck, handleResult, quilttSessionToken?.token]);

    return { resolvabilityMap, resolvabilityCheckers, isLoading: isBankDataLoading || isTokenLoading };
}
